import { useEffect, useState } from 'react';
import axios from 'axios';

/**
 * GTMInjector Component
 * 
 * This component fetches GTM settings from the backend and injects
 * the GTM Head Script and Body Noscript into the DOM.
 * 
 * The Head Script is injected before the closing </head> tag.
 * The Body Noscript is injected immediately after the opening <body> tag.
 */
const GTMInjector = () => {
  const [gtmHeadCode, setGtmHeadCode] = useState(null);
  const [gtmBodyCode, setGtmBodyCode] = useState(null);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    const fetchAndInjectGtm = async () => {
      try {
        const backendUrl = process.env.REACT_APP_API_URL;
        const response = await axios.get(`${backendUrl}Settings/GetGtmSettings`);

        if (response.data) {
          const headCode = response.data.gtmHeadCode || response.data.GtmHeadCode || '';
          const bodyCode = response.data.gtmBodyCode || response.data.GtmBodyCode || '';

          console.log('GTM Head Code received:', headCode ? 'Yes' : 'No');
          console.log('GTM Body Code received:', bodyCode ? 'Yes' : 'No');

          if (headCode || bodyCode) {
            setGtmHeadCode(headCode);
            setGtmBodyCode(bodyCode);
          } else {
            console.warn('GTM codes are empty. Please save GTM scripts in admin panel.');
          }
        }
      } catch (error) {
        // Silently fail if API doesn't exist yet or returns 404
        // This allows the app to work even if backend isn't ready
        console.error('GTM settings not available:', error.message);
        console.warn('Make sure GTM scripts are saved in Settings → Tracking / Integration');
      } finally {
        setIsLoaded(true);
      }
    };

    fetchAndInjectGtm();
  }, []);

  useEffect(() => {
    if (!isLoaded) return;

    // Inject GTM Head Script
    if (gtmHeadCode) {
      // Check if already injected to prevent duplicates
      const existingHeadScript = document.getElementById('gtm-head-script-marker');
      if (!existingHeadScript) {
        // Create a temporary container to parse the HTML
        const tempContainer = document.createElement('div');
        tempContainer.style.display = 'none';
        tempContainer.innerHTML = gtmHeadCode;
        
        // Insert before closing </head>
        const head = document.head || document.getElementsByTagName('head')[0];
        if (head) {
          // Extract and inject script tags
          const scripts = tempContainer.getElementsByTagName('script');
          console.log('Found', scripts.length, 'script tag(s) in GTM Head Code');
          
          Array.from(scripts).forEach((script, index) => {
            const newScript = document.createElement('script');
            
            // Handle script with src attribute
            if (script.src) {
              newScript.src = script.src;
              console.log(`Injecting GTM script ${index + 1} with src:`, script.src);
            }
            
            // Handle inline script
            const scriptContent = script.textContent || script.innerHTML || script.innerText;
            if (scriptContent && !script.src) {
              newScript.textContent = scriptContent;
              console.log(`Injecting GTM inline script ${index + 1}`);
            }
            
            // Copy all attributes
            Array.from(script.attributes).forEach((attr) => {
              if (attr.name !== 'src') {
                newScript.setAttribute(attr.name, attr.value);
              }
            });
            
            head.appendChild(newScript);
            console.log('GTM script injected successfully');
          });
          
          // Initialize dataLayer if it doesn't exist
          if (typeof window !== 'undefined') {
            window.dataLayer = window.dataLayer || [];
            console.log('dataLayer initialized:', window.dataLayer);
          }
          
          // Create a marker to prevent duplicate injection
          const marker = document.createElement('div');
          marker.id = 'gtm-head-script-marker';
          marker.style.display = 'none';
          head.appendChild(marker);
        }
      } else {
        console.log('GTM Head Script already injected, skipping...');
      }
    } else {
      console.warn('GTM Head Code is empty');
    }

    // Inject GTM Body Noscript
    if (gtmBodyCode) {
      // Check if already injected to prevent duplicates
      const existingBodyScript = document.getElementById('gtm-body-script-marker');
      if (!existingBodyScript) {
        // Create a temporary container to parse the HTML
        const tempContainer = document.createElement('div');
        tempContainer.style.display = 'none';
        tempContainer.innerHTML = gtmBodyCode;
        
        // Insert immediately after opening <body>
        const body = document.body || document.getElementsByTagName('body')[0];
        if (body) {
          // Extract and inject noscript tags
          const noscripts = tempContainer.getElementsByTagName('noscript');
          Array.from(noscripts).forEach((noscript) => {
            const newNoscript = document.createElement('noscript');
            newNoscript.innerHTML = noscript.innerHTML;
            // Copy all attributes
            Array.from(noscript.attributes).forEach((attr) => {
              newNoscript.setAttribute(attr.name, attr.value);
            });
            if (body.firstChild) {
              body.insertBefore(newNoscript, body.firstChild);
            } else {
              body.appendChild(newNoscript);
            }
          });
          
          // Create a marker to prevent duplicate injection
          const marker = document.createElement('div');
          marker.id = 'gtm-body-script-marker';
          marker.style.display = 'none';
          if (body.firstChild) {
            body.insertBefore(marker, body.firstChild);
          } else {
            body.appendChild(marker);
          }
        }
      }
    }
  }, [gtmHeadCode, gtmBodyCode, isLoaded]);

  // This component doesn't render anything visible
  return null;
};

export default GTMInjector;

